import { Button, Link, Option, Radio, RadioGroup, Text } from '@fluentui/react-components';
import { AddCircleRegular, SubtractCircleRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { dateTimeStringFormat } from '../../../common/date/datetime-string-format';
import { useStores } from '../../../common/hooks';
import { EventAttendee, maxAttendeesInEvent, WinnerEvent } from '../../../models';
import { MAX_EVENT_ATTENDEE_FIELD_LENGTH } from '../../../models/schemas/questionnaire-wizard-validation.schema';
import { ValidationMessageList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput, SelectOptions } from '../../shared-components/form';
import './EventAttendeesSection.scss';

const EventAttendeesSectionImpl: React.FC = () => {
  const {
    questionnaireWizardPageUiStore,
  } = useStores();

  const isBeingValidated = questionnaireWizardPageUiStore.isBeingValidated('event-attendees');

  const handleEventAttendingChange = (eventId: number, value: string): void => {
    if (value === 'yes') questionnaireWizardPageUiStore.setEventAttending(eventId, true);
    if (value === 'no') questionnaireWizardPageUiStore.setEventAttending(eventId, false);
  };

  const renderEventAttendeeInputs = (isFoodEvent: boolean, eventAttendee: EventAttendee): React.JSX.Element => {
    const validation = questionnaireWizardPageUiStore.eventAttendeeValidations[questionnaireWizardPageUiStore.questionnaire.eventAttendees.indexOf(eventAttendee)];
    const isDisabled = !eventAttendee.isAttending;

    return (
      <FlexBox
        isFullWidth
        direction='column'
        className='event-attendee-inputs'
      >
        <FlexBox
          isFullWidth
          aligns='start'
          wrap
          className='event-attendee-inputs__row'
        >
          <FlexBox
            isFullWidth
            aligns='start'
            className='row__sub-row'
          >
            <SelectOptions
              className='field--prefix'
              label='Prefix'
              onOptionSelect={(optionData) => questionnaireWizardPageUiStore.setEventAttendeeField(
                eventAttendee,
                {
                  key: 'prefix',
                  value: optionData.optionValue ?? '',
                },
              )}
              dropdownProps={{
                inlinePopup: true,
                disabled: isDisabled,
                value: eventAttendee.prefix ? eventAttendee.prefix.trim() : '',
                selectedOptions: [eventAttendee.prefix.trim()],
              }}
            >
              <Option value='' text='' />
              <Option value='Mr.'>Mr.</Option>
              <Option value='Mrs.'>Mrs.</Option>
              <Option value='Ms.'>Ms.</Option>
            </SelectOptions>
            <FormInput
              required
              label='First name'
              validationMessage={
                isBeingValidated && validation.fieldErrors.firstName?.length
                  ? <ValidationMessageList messages={validation.fieldErrors.firstName} />
                  : undefined
              }
              onChange={(firstName) => questionnaireWizardPageUiStore.setEventAttendeeField(
                eventAttendee,
                {
                  key: 'firstName',
                  value: firstName,
                },
              )}
              inputProps={{
                type: 'text',
                maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
                disabled: isDisabled,
                value: eventAttendee.firstName,
              }}
            />
          </FlexBox>
          <FormInput
            required
            label='Last name'
            validationMessage={
              isBeingValidated && validation.fieldErrors.lastName?.length
                ? <ValidationMessageList messages={validation.fieldErrors.lastName} />
                : undefined
            }
            onChange={(lastName) => questionnaireWizardPageUiStore.setEventAttendeeField(
              eventAttendee,
              {
                key: 'lastName',
                value: lastName,
              },
            )}
            inputProps={{
              type: 'text',
              maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
              disabled: isDisabled,
              value: eventAttendee.lastName,
            }}
          />
        </FlexBox>
        <FlexBox
          isFullWidth
          aligns='start'
          wrap
          className='event-attendee-inputs__row'
        >
          <FormInput
            label='Title'
            validationMessage={
              isBeingValidated && validation.fieldErrors.title?.length
                ? <ValidationMessageList messages={validation.fieldErrors.title} />
                : undefined
            }
            onChange={(title) => questionnaireWizardPageUiStore.setEventAttendeeField(
              eventAttendee,
              {
                key: 'title',
                value: title,
              },
            )}
            inputProps={{
              type: 'text',
              maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
              disabled: isDisabled,
              value: eventAttendee.title,
            }}
          />
          <FormInput
            required
            label='Email'
            validationMessage={
              isBeingValidated && validation.fieldErrors.emailAddress?.length
                ? <ValidationMessageList messages={validation.fieldErrors.emailAddress} />
                : undefined
            }
            onChange={(emailAddress) => questionnaireWizardPageUiStore.setEventAttendeeField(
              eventAttendee,
              {
                key: 'emailAddress',
                value: emailAddress.trim(),
              },
            )}
            inputProps={{
              type: 'text',
              maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
              disabled: isDisabled,
              value: eventAttendee.emailAddress,
            }}
          />
        </FlexBox>
        {
          isFoodEvent &&
          <>
            <FlexBox
              isFullWidth
              aligns='start'
              wrap
              className='event-attendee-inputs__row'
            >
              <FormInput
                required
                label='Primary Language'
                validationMessage={
                  isBeingValidated && validation.fieldErrors.primaryLanguage?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.primaryLanguage} />
                    : undefined
                }
                onChange={(primaryLanguage) =>
                  questionnaireWizardPageUiStore.setEventAttendeeField(
                    eventAttendee,
                    {
                      key: 'primaryLanguage',
                      value: primaryLanguage,
                    },
                  )}
                inputProps={{
                  type: 'text',
                  maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
                  disabled: isDisabled,
                  value: eventAttendee.primaryLanguage,
                }}
              />
              <FormInput
                label='Dietary requests'
                validationMessage={
                  isBeingValidated && validation.fieldErrors.dietaryRequests?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.dietaryRequests} />
                    : undefined
                }
                onChange={(dietaryRequests) =>
                  questionnaireWizardPageUiStore.setEventAttendeeField(
                    eventAttendee,
                    {
                      key: 'dietaryRequests',
                      value: dietaryRequests,
                    },
                  )}
                inputProps={{
                  type: 'text',
                  maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
                  disabled: isDisabled,
                  value: eventAttendee.dietaryRequests,
                }}
              />
            </FlexBox>
            <FlexBox
              isFullWidth
              aligns='start'
              wrap
              className='event-attendee-inputs__row'
            >
              <FormInput
                label='Accessibility needs'
                validationMessage={
                  isBeingValidated && validation.fieldErrors.accessibilityNeeds?.length
                    ? (
                      <ValidationMessageList
                        messages={validation.fieldErrors.accessibilityNeeds}
                      />
                    )
                    : undefined
                }
                onChange={(accessibilityNeeds) =>
                  questionnaireWizardPageUiStore.setEventAttendeeField(
                    eventAttendee,
                    {
                      key: 'accessibilityNeeds',
                      value: accessibilityNeeds,
                    },
                  )}
                inputProps={{
                  type: 'text',
                  maxLength: MAX_EVENT_ATTENDEE_FIELD_LENGTH,
                  disabled: isDisabled,
                  value: eventAttendee.accessibilityNeeds,
                }}
              />
            </FlexBox>
          </>
        }
      </FlexBox>
    )
  }

  const getEventTime = (winnerEvent: WinnerEvent): string => {
    return winnerEvent.startTime && winnerEvent.endTime ?
      `${dateTimeStringFormat(`1000-01-01T${winnerEvent.startTime}`, 'p')} - ${dateTimeStringFormat(`1000-01-01T${winnerEvent.endTime}`, 'p')} PST` :
      'Time TBD';
  }

  const renderEventAttendeeForm = (winnerEvent: WinnerEvent, attendees: EventAttendee[], primaryAttendee: EventAttendee): React.JSX.Element => {
    const isFoodEvent = winnerEvent.eventType === 'Food';
    return (
      <FlexBox
        key={winnerEvent.eventId}
        isFullWidth
        direction='column'
        aligns='start'
        className='event-attendees-section__group'
      >
        <Text
          as='h3'
          block
          weight='semibold'
          size={400}
        >
          {`${winnerEvent.displayName} | ${winnerEvent.isTimeTBD ? 'Date and time TBD' : `${dateTimeStringFormat(winnerEvent.eventDate, 'EEEE MMMM d, yyyy')}, ${getEventTime(winnerEvent)}`}`}
        </Text>
        <FlexBox
          direction='column'
          aligns='start'
          className='attending-question'
        >
          <Text>Will you be sending an attendee to this event?</Text>
          <RadioGroup
            layout='horizontal'
            value={primaryAttendee.isAttending ? 'yes' : 'no'}
            onChange={(_, data) => handleEventAttendingChange(winnerEvent.eventId, data.value)}
          >
            <Radio value='yes' label='Yes' />
            <Radio value='no' label='No' />
          </RadioGroup>
        </FlexBox>
        {renderEventAttendeeInputs(isFoodEvent, primaryAttendee)}
        {
          winnerEvent.eventType === 'Multi Attendee' && attendees.length > 1 &&
          attendees.map((attendee, index) => {
            if (index === 0) return null;
            return (
              <FlexBox
                key={index}
                isFullWidth
                direction='column'
                aligns='start'
                className='event-attendees-section__additional'
              >
                <Button
                  appearance='subtle'
                  icon={<SubtractCircleRegular />}
                  onClick={() => questionnaireWizardPageUiStore.removeEventAttendee(attendee)}
                >
                  Remove attendee
                </Button>
                {renderEventAttendeeInputs(isFoodEvent, attendee)}
              </FlexBox>
            );
          })
        }
        {
          winnerEvent.eventType === 'Multi Attendee' && attendees.length < maxAttendeesInEvent &&
          <Button
            appearance='subtle'
            icon={<AddCircleRegular />}
            onClick={() => questionnaireWizardPageUiStore.addEventAttendee(
              winnerEvent.eventId,
              primaryAttendee.isAttending,
            )}
          >
            Add additional attendee
          </Button>
        }
      </FlexBox>
    );
  }

  return (
    <>
      <FlexBox
        isFullWidth
        direction='column'
        aligns='start'
        className='event-attendees-section'
        as='section'
      >
        {
          questionnaireWizardPageUiStore.winnerEvents.map((winnerEvent) => {
            const eventAttendees = questionnaireWizardPageUiStore.getEventAttendees(winnerEvent.eventId);
            const primaryAttendee = eventAttendees[0];
            if (!primaryAttendee) return null;

            return renderEventAttendeeForm(winnerEvent, eventAttendees, primaryAttendee);
          })
        }
        <Link
          href='https://privacy.microsoft.com/en-us/privacystatement'
          target='_blank'
          rel='noopener noreferrer'
        >
          Privacy Statement
        </Link>
      </FlexBox>
    </>
  );
}

export const EventAttendeesSection = observer(EventAttendeesSectionImpl);
